import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import userService from "../api/userService";
import { hasRoleAccess } from "../utils/rolePermissions";
import {
  NAV_SECTIONS,
  ROLE_LABELS,
  buildDesktopEntries,
  flattenDesktopEntries,
  isNavPathActive,
} from "./navConfig";

const NAV_GAP = 6;
const COMPACT_QUERY = "(max-width: 720px)";
const NAV_BG = "#262626";
const NAV_PRIMARY = "#1975d1";
const OPEN_DELAY = 80;
const CLOSE_DELAY = 140;

const navItemSx = (active) => ({
  minWidth: 0,
  flexShrink: 0,
  boxSizing: "border-box",
  textTransform: "none",
  whiteSpace: "nowrap",
  borderRadius: "10px",
  px: 1.15,
  py: 0.45,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  lineHeight: 1.25,
  color: active ? "#fff" : "rgba(255,255,255,0.78)",
  bgcolor: active ? "rgba(25, 117, 209, 0.32)" : "transparent",
  boxShadow: active
    ? "inset 0 0 0 1px rgba(25, 117, 209, 0.45)"
    : "inset 0 0 0 1px transparent",
  transition:
    "background-color 180ms ease, box-shadow 180ms ease, color 180ms ease",
  "& .MuiButton-startIcon": {
    m: 0,
    mr: 0.7,
    "& > *:nth-of-type(1)": { fontSize: 17 },
  },
  "& .MuiButton-endIcon": {
    m: 0,
    ml: 0.25,
    width: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& > *:nth-of-type(1)": { fontSize: 16 },
  },
  "&:hover": {
    bgcolor: active ? "rgba(25, 117, 209, 0.42)" : "rgba(255,255,255,0.08)",
  },
});

const menuPaperSx = {
  mt: 1,
  minWidth: 248,
  borderRadius: "16px",
  bgcolor: "rgba(30, 30, 30, 0.96)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 18px 48px rgba(0,0,0,0.42)",
  backgroundImage: "none",
  backdropFilter: "blur(18px)",
  overflow: "hidden",
  "& .MuiMenuItem-root": {
    color: "rgba(255,255,255,0.88)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
  },
};

const dropdownPaperSx = {
  minWidth: 248,
  borderRadius: "16px",
  bgcolor: "rgba(22, 22, 22, 0.96)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 22px 50px rgba(0,0,0,0.45)",
  backgroundImage: "none",
  backdropFilter: "blur(16px)",
  overflow: "hidden",
};

const popperMods = [
  { name: "offset", options: { offset: [0, 0] } },
  { name: "preventOverflow", options: { padding: 8 } },
];

function getInitials(user) {
  if (user?.name) {
    const parts = user.name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }
  if (user?.email) return user.email.substring(0, 2).toUpperCase();
  return "US";
}

function countFit(containerWidth, itemWidths, moreWidth) {
  const n = itemWidths.length;
  for (let k = n; k >= 0; k -= 1) {
    const shown = itemWidths.slice(0, k);
    const shownWidth =
      shown.reduce((sum, w) => sum + w, 0) + NAV_GAP * Math.max(0, shown.length - 1);
    const needMore = k < n;
    const extra = needMore ? (shown.length ? NAV_GAP : 0) + moreWidth : 0;
    if (shownWidth + extra <= containerWidth) return k;
  }
  return 0;
}

function NavDivider() {
  return (
    <Box
      aria-hidden
      sx={{
        width: "1px",
        height: 16,
        flexShrink: 0,
        mx: 0.35,
        borderRadius: "1px",
        bgcolor: "rgba(255,255,255,0.16)",
        alignSelf: "center",
      }}
    />
  );
}

function NavItem({ item, active, onClick }) {
  const Icon = item.icon;
  const disabled = Boolean(item.disabled);
  return (
    <Button
      disableElevation
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      startIcon={<Icon />}
      sx={{
        ...navItemSx(active && !disabled),
        ...(disabled && {
          opacity: 0.45,
          cursor: "not-allowed",
          "&.Mui-disabled": { color: "rgba(255,255,255,0.55)" },
        }),
      }}
    >
      {item.text}
    </Button>
  );
}

function NavGroupTrigger({ label, active, open, onClick, buttonRef, ...rest }) {
  return (
    <Button
      ref={buttonRef}
      disableElevation
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={open}
      endIcon={
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            opacity: 0.7,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      }
      sx={navItemSx(active || open)}
      {...rest}
    >
      {label}
    </Button>
  );
}

function NavDropdownItem({ item, active, onClick }) {
  const Icon = item.icon;
  const disabled = Boolean(item.disabled);
  return (
    <ButtonBase
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.15,
        width: "100%",
        textAlign: "left",
        px: 0.85,
        py: 0.75,
        borderRadius: "12px",
        color: disabled
          ? "rgba(255,255,255,0.38)"
          : active
          ? "#fff"
          : "rgba(255,255,255,0.88)",
        bgcolor: active && !disabled ? "rgba(25, 117, 209, 0.26)" : "transparent",
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background-color 160ms ease",
        "&:hover": {
          bgcolor: disabled
            ? "transparent"
            : active
            ? "rgba(25, 117, 209, 0.36)"
            : "rgba(255,255,255,0.07)",
        },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: active ? "rgba(25, 117, 209, 0.28)" : "rgba(255,255,255,0.06)",
          color: active ? "#7eb6f0" : "rgba(255,255,255,0.62)",
          transition: "background-color 160ms ease, color 160ms ease",
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: active ? 700 : 600,
            letterSpacing: "-0.01em",
            lineHeight: 1.25,
          }}
        >
          {item.text}
        </Typography>
        {item.hint && (
          <Typography
            sx={{
              fontSize: 11,
              color: "rgba(255,255,255,0.4)",
              lineHeight: 1.25,
              mt: 0.15,
            }}
          >
            {item.hint}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

function NavDropdown({
  entry,
  pathname,
  open,
  anyOpen,
  onOpen,
  onClose,
  onNavigate,
  showSections = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const active = entry.items.some((item) => isNavPathActive(pathname, item.path));

  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };

  const handleEnter = () => {
    clearTimers();
    if (open || anyOpen) {
      onOpen();
      return;
    }
    openTimer.current = window.setTimeout(onOpen, OPEN_DELAY);
  };

  const handleLeave = () => {
    clearTimers();
    closeTimer.current = window.setTimeout(onClose, CLOSE_DELAY);
  };

  useEffect(() => () => clearTimers(), []);

  return (
    <Box ref={setAnchorEl} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <NavGroupTrigger
        label={entry.label}
        active={active}
        open={open}
        onClick={() => (open ? onClose() : onOpen())}
      />
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={popperMods}
        sx={{ zIndex: 1400, pointerEvents: "none" }}
      >
        <Box
          data-nav-dropdown=""
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          sx={{
            pointerEvents: "auto",
            pt: 1.25,
            px: 0.25,
            pb: 0.25,
          }}
        >
          <Paper elevation={0} sx={dropdownPaperSx}>
            {!showSections && (
              <Box sx={{ px: 1.5, pt: 1.2, pb: 0.55 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.38)",
                  }}
                >
                  {entry.label}
                </Typography>
              </Box>
            )}
            <Box sx={{ px: 0.75, pt: showSections ? 0.75 : 0, pb: 0.75, display: "grid", gap: 0.15 }}>
              {entry.items.map((item, index) => {
                const prev = entry.items[index - 1];
                const showLabel = showSections && item.section && item.section !== prev?.section;
                return (
                  <Box key={item.path}>
                    {showLabel && (
                      <Typography
                        sx={{
                          display: "block",
                          px: 1.1,
                          pt: index === 0 ? 0.35 : 1.15,
                          pb: 0.45,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.38)",
                        }}
                      >
                        {item.section}
                      </Typography>
                    )}
                    <NavDropdownItem
                      item={item}
                      active={!item.disabled && isNavPathActive(pathname, item.path)}
                      onClick={() => !item.disabled && onNavigate(item.path)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      </Popper>
    </Box>
  );
}

function ClusterEntry({
  entry,
  pathname,
  open,
  anyOpen,
  onOpen,
  onClose,
  onNavigate,
  hideTrailingDivider,
}) {
  if (entry.type === "divider") {
    return hideTrailingDivider ? null : <NavDivider />;
  }
  if (entry.type === "group") {
    return (
      <NavDropdown
        entry={entry}
        pathname={pathname}
        open={open}
        anyOpen={anyOpen}
        onOpen={onOpen}
        onClose={onClose}
        onNavigate={onNavigate}
      />
    );
  }
  return (
    <NavItem
      item={entry.item}
      active={!entry.item.disabled && isNavPathActive(pathname, entry.item.path)}
      onClick={() => !entry.item.disabled && onNavigate(entry.item.path)}
    />
  );
}

function MeasureEntry({ entry }) {
  if (entry.type === "divider") return <NavDivider />;
  if (entry.type === "group") {
    return <NavGroupTrigger label={entry.label} active={false} open={false} />;
  }
  return <NavItem item={entry.item} active={false} onClick={() => {}} />;
}

export default function AppNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    effectiveUser,
    isPreviewing,
    role,
    logout,
    setPrivilegePreview,
    clearPrivilegePreview,
  } = useAuth();
  const accessUser = effectiveUser || user;
  const canViewAs = user?.role === "admin";
  const [people, setPeople] = useState([]);
  const [rolesOpen, setRolesOpen] = useState(false);

  const navRef = useRef(null);
  const clusterRef = useRef(null);
  const measureRef = useRef(null);
  const moreMeasureRef = useRef(null);

  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches
  );
  const [visibleCount, setVisibleCount] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userEl, setUserEl] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [showUserName, setShowUserName] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches
  );

  const userRole = role ?? user?.role;
  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => hasRoleAccess(userRole, item.roles)),
      })).filter((section) => section.items.length > 0),
    [userRole]
  );

  const entries = useMemo(() => buildDesktopEntries(sections), [sections]);

  useEffect(() => {
    const compactMq = window.matchMedia(COMPACT_QUERY);
    const nameMq = window.matchMedia("(min-width: 900px)");
    const onCompact = () => setCompact(compactMq.matches);
    const onName = () => setShowUserName(nameMq.matches);
    onCompact();
    onName();
    compactMq.addEventListener("change", onCompact);
    nameMq.addEventListener("change", onName);
    return () => {
      compactMq.removeEventListener("change", onCompact);
      nameMq.removeEventListener("change", onName);
    };
  }, []);

  useEffect(() => {
    if (compact) setOpenGroup(null);
  }, [compact]);

  useEffect(() => {
    if (!openGroup) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpenGroup(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openGroup]);

  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        "--app-nav-height",
        `${el.offsetHeight}px`
      );
    };

    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    syncHeight();
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--app-nav-height");
    };
  }, [compact, drawerOpen]);

  useLayoutEffect(() => {
    if (compact) {
      setVisibleCount(entries.length);
      return undefined;
    }

    const cluster = clusterRef.current;
    const measureRow = measureRef.current;
    if (!cluster || !measureRow) return undefined;

    const measure = () => {
      const kids = Array.from(measureRow.children);
      const itemWidths = kids.map((node) => node.getBoundingClientRect().width);
      const moreWidth = moreMeasureRef.current
        ? moreMeasureRef.current.getBoundingClientRect().width
        : 88;
      const next = countFit(cluster.clientWidth, itemWidths, moreWidth);
      setVisibleCount(next);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(cluster);
    measure();
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => ro.disconnect();
  }, [compact, entries]);

  const ready = compact || visibleCount !== null;
  const shownCount = compact ? 0 : visibleCount ?? entries.length;
  const visibleEntries = compact ? [] : entries.slice(0, shownCount);
  const overflowEntries = compact ? entries : entries.slice(shownCount);
  const overflowItems = flattenDesktopEntries(overflowEntries);
  const lastVisible = visibleEntries[visibleEntries.length - 1];
  const hideTrailingDivider = lastVisible?.type === "divider";

  const go = (path) => {
    navigate(path);
    setDrawerOpen(false);
    setOpenGroup(null);
  };

  const enterAsUser = async (person) => {
    const id = person.id || person._id;
    if (!id) return;
    const mine = user?.id || user?._id;
    if (!isPreviewing && String(id) === String(mine)) {
      setUserEl(null);
      return;
    }
    try {
      const res = await userService.previewPrivileges(id);
      const nextUser = res.data?.user ?? res.data?.data;
      if (!nextUser) return;
      setPrivilegePreview(nextUser);
      setUserEl(null);
      setRolesOpen(false);
      navigate("/");
    } catch {
      /* la sesión real del admin sigue intacta */
    }
  };

  const handleLogout = () => {
    logout();
    setUserEl(null);
    setDrawerOpen(false);
    navigate("/login");
  };

  const handleClusterAway = (event) => {
    if (event.target.closest?.("[data-nav-dropdown]")) return;
    setOpenGroup(null);
  };

  const initials = getInitials(accessUser);
  const displayName = accessUser?.name || accessUser?.email || "Usuario";
  const roleLabel = ROLE_LABELS[userRole] || userRole || "";
  const fieldMode = compact && userRole === "technician";

  return (
    <>
      <AppBar
        ref={navRef}
        position="static"
        elevation={0}
        className="no-print"
        sx={{
          flexShrink: 0,
          color: "#fff",
          backgroundColor: NAV_BG,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            "&.MuiToolbar-root": {
              minHeight: fieldMode ? 48 : "clamp(48px, 7.2vh, 56px)",
              height: fieldMode ? "auto" : "clamp(48px, 7.2vh, 56px)",
              py: fieldMode ? 0.75 : 0,
            },
            px: { xs: 1, sm: 1.5, md: 2 },
            gap: { xs: 0.75, sm: 1.25 },
            display: "flex",
            alignItems: "center",
          }}
        >
          {(compact || shownCount === 0) && !fieldMode && (
            <IconButton
              aria-label="Abrir menú"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: "rgba(255,255,255,0.88)", mr: 0.25 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            onClick={() => go("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              flexShrink: 0,
              minWidth: 0,
              mr: { xs: 0.5, md: 1 },
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Bremar"
              sx={{
                height: "clamp(26px, 4.4vh, 34px)",
                width: "auto",
                maxWidth: 120,
                objectFit: "contain",
                borderRadius: "6px",
                display: "block",
              }}
            />
          </Box>

          {!compact && (
            <ClickAwayListener onClickAway={handleClusterAway}>
              <Box
                ref={clusterRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  ref={measureRef}
                  aria-hidden
                  sx={{
                    position: "absolute",
                    visibility: "hidden",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: `${NAV_GAP}px`,
                    whiteSpace: "nowrap",
                    left: 0,
                    top: 0,
                  }}
                >
                  {entries.map((entry) => (
                    <MeasureEntry key={`m-${entry.key}`} entry={entry} />
                  ))}
                </Box>

                <Button
                  ref={moreMeasureRef}
                  aria-hidden
                  tabIndex={-1}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    ...navItemSx(false),
                    position: "absolute",
                    visibility: "hidden",
                    pointerEvents: "none",
                  }}
                >
                  Más
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${NAV_GAP}px`,
                    minWidth: 0,
                    opacity: ready ? 1 : 0,
                  }}
                >
                  {visibleEntries.map((entry) => (
                    <ClusterEntry
                      key={entry.key}
                      entry={entry}
                      pathname={location.pathname}
                      open={openGroup === entry.key}
                      anyOpen={Boolean(openGroup)}
                      hideTrailingDivider={hideTrailingDivider}
                      onOpen={() => setOpenGroup(entry.key)}
                      onClose={() =>
                        setOpenGroup((current) => (current === entry.key ? null : current))
                      }
                      onNavigate={go}
                    />
                  ))}

                  {overflowItems.length > 0 && shownCount > 0 && (
                    <NavDropdown
                      entry={{ key: "more", label: "Más", items: overflowItems }}
                      showSections
                      pathname={location.pathname}
                      open={openGroup === "more"}
                      anyOpen={Boolean(openGroup)}
                      onOpen={() => setOpenGroup("more")}
                      onClose={() =>
                        setOpenGroup((current) => (current === "more" ? null : current))
                      }
                      onNavigate={go}
                    />
                  )}
                </Box>
              </Box>
            </ClickAwayListener>
          )}

          {fieldMode && (
            <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 650,
                  lineHeight: 1.25,
                  color: "#fff",
                  overflowWrap: "anywhere",
                }}
              >
                {displayName}
              </Typography>
            </Box>
          )}

          {compact && !fieldMode && <Box sx={{ flex: 1, minWidth: 0 }} />}

          <Tooltip title="Cuenta" placement="bottom">
            <Button
              onClick={(e) => {
                setOpenGroup(null);
                setUserEl(e.currentTarget);
                if (canViewAs && people.length === 0) {
                  userService
                    .getAllUsers()
                    .then((res) => {
                      const list = res.data?.data || res.data?.users || [];
                      setPeople(Array.isArray(list) ? list : []);
                    })
                    .catch(() => {});
                }
              }}
              sx={{
                ml: "auto",
                flexShrink: 0,
                minWidth: 0,
                maxWidth: { xs: 48, sm: 220 },
                textTransform: "none",
                color: "#fff",
                borderRadius: "10px",
                px: { xs: 0.4, sm: 0.75 },
                py: 0.35,
                gap: 1,
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              <Avatar
                src={user?.avatar || undefined}
                sx={{
                  width: fieldMode ? 28 : 32,
                  height: fieldMode ? 28 : 32,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: user?.avatar ? "transparent" : NAV_PRIMARY,
                  color: "#fff",
                }}
              >
                {!user?.avatar ? initials : ""}
              </Avatar>
              {showUserName && !fieldMode && (
                <Box sx={{ textAlign: "left", minWidth: 0, display: { xs: "none", sm: "block" } }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, maxWidth: 140 }}
                  >
                    {displayName}
                  </Typography>
                  {roleLabel && (
                    <Typography
                      noWrap
                      sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.2, maxWidth: 140 }}
                    >
                      {roleLabel}
                    </Typography>
                  )}
                </Box>
              )}
            </Button>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {isPreviewing && (
        <Box
          className="no-print"
          sx={{
            flexShrink: 0,
            px: 2,
            py: 0.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: "#1e3a5f",
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
            Viendo la app como {displayName}
            {roleLabel ? ` · ${roleLabel}` : ""}
          </Typography>
          <Button
            size="small"
            onClick={() => {
              clearPrivilegePreview();
              navigate("/");
            }}
            sx={{
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              minWidth: 0,
            }}
          >
            Volver a mi cuenta
          </Button>
        </Box>
      )}

      <Menu
        anchorEl={userEl}
        open={Boolean(userEl)}
        onClose={() => setUserEl(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: { sx: menuPaperSx },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{displayName}</Typography>
          {roleLabel && (
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{roleLabel}</Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        {canViewAs && [
            <MenuItem
              key="view-as"
              onClick={(event) => {
                event.stopPropagation();
                setRolesOpen((open) => !open);
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mx: 0.75,
                my: 0.25,
                borderRadius: "10px",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>
                  Ver como
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 650, color: "#fff" }}>
                  {isPreviewing ? displayName : "Mi cuenta"}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: "#94a3b8",
                  transform: rolesOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            </MenuItem>,
            <Collapse key="view-as-list" in={rolesOpen} timeout="auto" unmountOnExit>
              <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                {people.map((person) => {
                  const id = person.id || person._id;
                  const active = String(id) === String(accessUser?.id || accessUser?._id);
                  return (
                    <MenuItem
                      key={id}
                      onClick={() => enterAsUser(person)}
                      sx={{
                        pl: 3,
                        mx: 0.75,
                        my: 0.15,
                        borderRadius: "10px",
                        fontWeight: active ? 700 : 500,
                        bgcolor: active ? "rgba(25, 117, 209, 0.28)" : "transparent",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: "inherit" }}>
                          {person.name || person.email}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 11, color: "#94a3b8" }}>
                          {ROLE_LABELS[person.role] || person.role}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            </Collapse>,
        ]}
        {isPreviewing && (
          <MenuItem
            onClick={() => {
              clearPrivilegePreview();
              setUserEl(null);
              setRolesOpen(false);
              navigate("/");
            }}
            sx={{ mx: 0.75, my: 0.25, borderRadius: "10px", fontWeight: 700 }}
          >
            Salir de la previsualización
          </MenuItem>
        )}
        <MenuItem
          onClick={handleLogout}
          sx={{ color: "#f87171", gap: 1, fontWeight: 600, mx: 0.75, my: 0.5, borderRadius: "10px" }}
        >
          <LogoutIcon sx={{ fontSize: 18 }} />
          Cerrar sesión
        </MenuItem>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 10, color: "#64748b", letterSpacing: "0.02em" }}>
            FE BRM2644.2 · BE SRV2644.0
          </Typography>
        </Box>
      </Menu>

      {!fieldMode && (
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "min(300px, 88vw)",
            bgcolor: NAV_BG,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            minHeight: 56,
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="Bremar"
            sx={{ height: 32, width: "auto" }}
          />
          <IconButton
            aria-label="Cerrar menú"
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "rgba(255,255,255,0.88)" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", py: 1.25, px: 0.5 }}>
          {sections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <Box
                key={section.id || section.label || "inicio"}
                sx={{
                  mx: 0.75,
                  mb: 1,
                  p: 0.6,
                  borderRadius: "14px",
                  bgcolor: section.label ? "rgba(255,255,255,0.035)" : "transparent",
                }}
              >
                {section.label && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.15,
                      pt: 0.65,
                      pb: 0.55,
                    }}
                  >
                    {SectionIcon && (
                      <SectionIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.38)" }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.38)",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontSize: 10,
                      }}
                    >
                      {section.label}
                    </Typography>
                  </Box>
                )}
                <List disablePadding>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const disabled = Boolean(item.disabled);
                    const active = !disabled && isNavPathActive(location.pathname, item.path);
                    return (
                      <ListItemButton
                        key={item.path}
                        disabled={disabled}
                        onClick={() => !disabled && go(item.path)}
                        selected={active}
                        sx={{
                          mx: 0.15,
                          my: 0.15,
                          borderRadius: "12px",
                          color: disabled ? "rgba(255,255,255,0.38)" : "rgba(255,255,255,0.82)",
                          opacity: disabled ? 0.75 : 1,
                          transition: "background-color 160ms ease",
                          "&.Mui-selected": {
                            bgcolor: "rgba(25, 117, 209, 0.32)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(25, 117, 209, 0.42)" },
                          },
                          "&:hover": {
                            bgcolor: disabled ? "transparent" : "rgba(255,255,255,0.08)",
                          },
                          "&.Mui-disabled": { opacity: 0.75 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: active
                                ? "rgba(25, 117, 209, 0.28)"
                                : "rgba(255,255,255,0.06)",
                              color: active ? "#7eb6f0" : "#94a3b8",
                            }}
                          >
                            <Icon sx={{ fontSize: 18 }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          secondary={item.hint}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: active ? 700 : 500,
                          }}
                          secondaryTypographyProps={{
                            fontSize: 11,
                            color: "rgba(255,255,255,0.38)",
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
            FE BRM2644.2 · BE SRV2644.0
          </Typography>
        </Box>
      </Drawer>
      )}
    </>
  );
}
